import { AxiosRequestConfig } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { ChatMessage } from './ChatTypes';
import { useCallChatApi } from './UseCallChatApi';
import { messagesAddToolResult, messagesHavePendingToolCalls } from './UseChatHelpers';

export interface CreateMessage {
    id?: string;
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: any[];
    createdAt?: Date;
    toolCallId?: string; // For creating 'tool' role messages with results
}

export interface UseChatOptions {
    api: string;
    initialMessages?: ChatMessage[];
    onInitRequest?: (requestConfig: AxiosRequestConfig) => AxiosRequestConfig;
    onStart?: () => void; // Called when a request is starting
    onFinish?: (message: ChatMessage) => void;
    onError?: (error: Error) => void;
}

const mergeInMessage = (messages: ChatMessage[], message: ChatMessage) => {
    const index = messages.findIndex(m => m.id === message.id);
    if (index !== -1) {
        const updated = [...messages];
        updated[index] = message;
        return updated;
    }
    return [...messages, message];
};

export function useChat(options: UseChatOptions) {
    const { initialMessages = [], } = options;

    const callChatApi = useCallChatApi();

    const [messages, setMessages] = useState<ChatMessage[]>(() => {
        return initialMessages.map(m => ({ ...m, id: m.id || uuid() }));
    });

    // Ref to ensure callbacks have access to the latest messages
    // This is particularly useful if `append` is memoized and `messages` state changes.
    const messagesRef = useRef(messages);
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    const submit = useCallback(async () => {

        options?.onStart?.();

        await callChatApi({
            api: options.api,
            messages: messagesRef.current,
            onInitRequest: options?.onInitRequest,
            onUpdate(liveMessage) {
                setMessages(prev => mergeInMessage(prev, liveMessage));
            },
            onFinish(finalMessage) {
                setMessages(prev => mergeInMessage(prev, finalMessage));
                options?.onFinish?.(finalMessage);
            },
            onError: (err) => {
                options?.onError?.(err);
            },
        });
    }, [callChatApi, options.api, options.onInitRequest, options.onStart, options.onFinish, options.onError]);

    const append = useCallback((message: CreateMessage) => {

        const newMessage: ChatMessage = {
            ...message,
            id: message.id || uuid(),
            createdAt: message.createdAt || new Date(),
        };

        // Update UI optimistically with user message
        // Use functional update to get the most recent `prevMessages`
        setMessages(prev => [...prev, newMessage]);
        messagesRef.current = [...messagesRef.current, newMessage];

    }, []);

    const addToolResult = useCallback((toolCallId: string, result: any) => {

        const next = messagesAddToolResult(messagesRef.current, toolCallId, result);

        // Update the messages
        setMessages(next);
        messagesRef.current = next;

        return !messagesHavePendingToolCalls(next);

    }, []);

    return {
        messages,
        setMessages,
        append,
        submit,
        addToolResult,
    };
}
